import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, ClipboardList, CalendarClock, FileText, ShieldCheck, Leaf } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { useAuth, homeFor } from "../context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1714976694867-bc0e012fab70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHx0aGVyYXBpc3QlMjBjb25zdWx0YXRpb258ZW58MHx8fHwxNzg4MDAwNjc3fDA&ixlib=rb-4.1.0&q=85";
const WORKPLACE_IMG = "https://images.unsplash.com/photo-1551434678-e076c223a692?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxjYWxtJTIwb2ZmaWNlJTIwd29ya2luZ3xlbnwwfHx8fDE3ODgwMDA2Nzd8MA&ixlib=rb-4.1.0&q=85";
const PROCESS_IMG = "https://images.unsplash.com/photo-1616740793717-0aca29b92221?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHx0aGVyYXBpc3QlMjBjb25zdWx0YXRpb258ZW58MHx8fHwxNzg4MDAwNjc3fDA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { icon: ClipboardList, title: "Online intake", text: "Tell us what the evaluation is for. The right measures are assigned automatically." },
  { icon: CheckCircle2, title: "Validated questionnaires", text: "Complete measures such as WHODAS 2.0, PHQ-9, GAD-7, PCL-5, and ASRS securely online." },
  { icon: CalendarClock, title: "Clinician interview", text: "A 60–90 minute structured video interview with a clinician licensed in your state." },
  { icon: FileText, title: "Integrated report", text: "Your clinician writes an individualized report with a functional analysis — never a questionnaire printout." },
  { icon: ShieldCheck, title: "QA review & delivery", text: "Every report passes a quality-assurance checklist before secure PDF delivery." },
];

const fade = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

export default function Landing() {
  const [products, setProducts] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const start = (slug) => {
    if (user) navigate("/app", { state: { product: slug } });
    else navigate(`/register?product=${slug || ""}`);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] noise-bg text-[#1C1C19]">
      <header className="sticky top-0 z-40 bg-[#FAFAF8]/90 backdrop-blur-xl border-b border-[#E5E5DF]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="brand-mark">
            <span className="h-9 w-9 rounded-full bg-[#1E3F20] flex items-center justify-center">
              <Leaf className="h-4 w-4 text-[#FAFAF8]" />
            </span>
            <div>
              <p className="font-heading text-lg font-semibold tracking-tight leading-none">Assessment Center</p>
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#5C5C57]">Psychological & Functional</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user ? (
              <Button data-testid="go-to-portal-btn" onClick={() => navigate(homeFor(user.role))}
                className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors px-6">
                Go to portal <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <>
                <Link to="/login" data-testid="login-nav-link"><Button variant="ghost" className="rounded-full text-[#1C1C19] hover:bg-[#E3DAC9]/50">Sign in</Button></Link>
                <Link to="/register" data-testid="register-nav-link">
                  <Button className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors px-6">Begin an evaluation</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={fade} className="lg:col-span-7">
            <p className="label-caps text-[#7E9B82] mb-5" data-testid="hero-eyebrow">Clinician-led · Telehealth · State-licensed</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]" data-testid="hero-heading">
              Professional psychological & functional evaluations, <em className="text-[#1E3F20]">online</em>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#5C5C57] max-w-xl" data-testid="hero-subtext">
              A comprehensive clinical interview, validated assessment measures, a functional analysis,
              and an individualized written report — delivered by licensed clinicians, reviewed through quality assurance.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-start-btn" onClick={() => start()}
                className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white px-8 h-12 text-base transition-colors">
                Begin an evaluation <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <a href="#products" data-testid="hero-explore-link">
                <Button variant="outline" className="rounded-full border-[#1E3F20]/30 text-[#1E3F20] hover:bg-[#E3DAC9]/40 px-8 h-12 text-base transition-colors">
                  Explore the five evaluations
                </Button>
              </a>
            </div>
            <p className="mt-6 text-xs text-[#5C5C57] max-w-md">
              Reports provide clinical documentation that may be submitted to the requesting organization.
              The receiving agency, employer, or school determines what evidence it accepts.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.15 } }} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#E3DAC9]/60 rounded-3xl -z-10 rotate-2" />
              <img src={HERO_IMG} alt="Clinician in a warm consultation" className="rounded-2xl w-full h-[420px] object-cover border border-[#E5E5DF]" data-testid="hero-image" />
            </div>
          </motion.div>
        </div>
      </section>

      <section id="products" className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-12">
          <p className="label-caps text-[#7E9B82] mb-4">The five evaluations</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight" data-testid="products-heading">
            One center. Five clinician-led products.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-8">
          {products.map((p, i) => (
            <motion.div key={p.slug} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
              transition={{ delay: i * 0.06 }}
              className={`card-warm p-8 flex flex-col hover:-translate-y-1 transition-transform duration-300 ${i === 0 ? "md:col-span-8 lg:col-span-7" : i === 1 ? "md:col-span-8 lg:col-span-5" : "md:col-span-4 lg:col-span-4"}`}
              data-testid={`product-card-${p.slug}`}>
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight">{p.name}</h3>
                <span className="shrink-0 text-sm font-bold text-[#1E3F20] bg-[#E3DAC9]/60 rounded-full px-4 py-1.5" data-testid={`product-price-${p.slug}`}>
                  ${p.price.toFixed(0)}
                </span>
              </div>
              <p className="mt-3 text-sm text-[#5C5C57] flex-1">{p.tagline}</p>
              <ul className="mt-4 space-y-1.5">
                {p.audience.slice(0, 3).map((a) => (
                  <li key={a} className="text-xs text-[#5C5C57] flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-[#7E9B82] shrink-0" />{a}</li>
                ))}
              </ul>
              <div className="mt-6 flex items-center justify-between">
                <span className="text-xs text-[#5C5C57]">{p.interview_minutes}-min interview · {p.report_pages} report</span>
                <Button data-testid={`product-start-${p.slug}`} onClick={() => start(p.slug)}
                  className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors" size="sm">
                  Begin <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </div>
            </motion.div>
          ))}
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
            className="md:col-span-4 lg:col-span-4 rounded-xl overflow-hidden border border-[#E5E5DF] relative min-h-[220px]">
            <img src={WORKPLACE_IMG} alt="Calm office setting" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[#1E3F20]/55" />
            <div className="relative p-8 h-full flex flex-col justify-end">
              <p className="font-heading text-2xl text-white font-semibold">Employer-facing documentation, done right.</p>
              <p className="text-white/80 text-sm mt-2">Functional limitations and potential supports — while the employer makes the final accommodation decision.</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-[#1E3F20] text-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="label-caps text-[#7E9B82] mb-4">How it works</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight" data-testid="process-heading">
              From referral question to signed report.
            </h2>
            <img src={PROCESS_IMG} alt="Structured clinical documentation" className="mt-8 rounded-xl h-56 w-full object-cover border border-white/10" />
          </div>
          <div className="lg:col-span-7 space-y-2">
            {STEPS.map((s, i) => (
              <motion.div key={s.title} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
                className="flex gap-5 p-5 rounded-xl hover:bg-white/5 transition-colors" data-testid={`process-step-${i}`}>
                <div className="h-11 w-11 shrink-0 rounded-full bg-[#7E9B82]/20 flex items-center justify-center">
                  <s.icon className="h-5 w-5 text-[#7E9B82]" />
                </div>
                <div>
                  <p className="font-semibold">{i + 1}. {s.title}</p>
                  <p className="text-sm text-white/70 mt-1">{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="label-caps text-[#7E9B82] mb-4">Transparent pricing</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Three levels of depth.</h2>
            <p className="mt-4 text-[#5C5C57] max-w-xl text-sm sm:text-base">
              Every evaluation is clinician-led. Deeper levels add interview time, record review, and collateral information.
              The standard comprehensive price for each product is shown above; your clinician will tell you if a case
              warrants a more complex workup before any additional charge.
            </p>
          </div>
          <div className="lg:col-span-5 grid gap-4">
            {[
              { name: "Basic", price: "$395", desc: "60-minute interview · 2–3 measures · 5–7 page report" },
              { name: "Comprehensive", price: "$595–695", desc: "75–90-minute interview · 3–5 measures · records review · 8–12 page report", featured: true },
              { name: "Complex", price: "$795–995", desc: "90-minute interview · multiple measures · collateral information · 12–15 page report" },
            ].map((t) => (
              <div key={t.name} data-testid={`pricing-tier-${t.name.toLowerCase()}`}
                className={`p-6 rounded-xl border ${t.featured ? "bg-[#1E3F20] text-white border-[#1E3F20]" : "card-warm"}`}>
                <div className="flex items-baseline justify-between">
                  <p className="font-heading text-xl font-semibold">{t.name}</p>
                  <p className={`font-bold ${t.featured ? "text-[#E3DAC9]" : "text-[#1E3F20]"}`}>{t.price}</p>
                </div>
                <p className={`text-sm mt-2 ${t.featured ? "text-white/75" : "text-[#5C5C57]"}`}>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-20">
        <div className="card-warm p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="honesty-section">
          <div className="lg:col-span-4">
            <p className="label-caps text-[#8C2A2A] mb-4">What we don't promise</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Honest scope, by design.</h2>
          </div>
          <div className="lg:col-span-8 text-sm text-[#5C5C57] space-y-3">
            <p>We never guarantee approval for disability, employment, or school determinations — the receiving agency, employer, or school ultimately decides what evidence it accepts.</p>
            <p>Screening measures such as the ASRS inform — but never by themselves establish — a diagnosis. Every conclusion is formed by your clinician through interview, observation, functional history, and validated measures, with limitations and contradictory evidence stated plainly.</p>
            <p>School-eligibility evaluations (IEP/IDEA) require additional procedures beyond this service; our college product documents functional limitations and potential supports for consideration by your institution.</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5E5DF]">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-full bg-[#1E3F20] flex items-center justify-center"><Leaf className="h-3.5 w-3.5 text-[#FAFAF8]" /></span>
            <p className="text-sm text-[#5C5C57]">Psychological & Functional Assessment Center</p>
          </div>
          <p className="text-xs text-[#5C5C57] max-w-lg">
            Clinician-led evaluations combining a comprehensive clinical interview, validated assessment measures,
            functional assessment, and an individualized written report.
          </p>
        </div>
      </footer>
    </div>
  );
}
